// components/APtokenCollection.js
import { useState, useEffect } from 'react';
import Web3 from 'web3';
import { contractABI, contractAddress } from '../contractConfig';

const ApTokenCollection = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [programOperator, setProgramOperator] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Select or take photos for for uploading
  const handleImageSelection = (event) => {
      const file = event.target.files?.[0];

      if (!file) return;

      const validTypes = ['image/jpeg', 'image/png'];
      const maxSize = 5 * 1024 * 1024; // 5 MB

      if (!validTypes.includes(file.type)) {
          setImageFile(null);
          setImagePreview('');
          setMessage('Invalid file type. Only JPEG and PNG are allowed.');
          event.target.value = '';
          return;
      }

      if (file.size > maxSize) {
          setImageFile(null);
          setImagePreview('');
          setMessage('File size exceeds 5 MB.');
          event.target.value = '';
          return;
      }

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setMessage('');
  };

  //Add cleanup for the preview URL
  useEffect(() => {
      return () => {
          if (imagePreview) {
              URL.revokeObjectURL(imagePreview);
          }
      };
  }, [imagePreview]);

  const uploadFileToIPFS = async (file) => {
    const formData = new FormData();
    formData.append('file', file, file.name);

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload file to IPFS: ${errorText}`);
      }

      const result = await response.json();
      return result.ipfsUri;
    } catch (error) {
      console.error('Error uploading image to IPFS:', error);
      throw error;
    }
  };

  const uploadToIPFS = async (data) => {
    try {
      const response = await fetch('/api/uploadMetadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload metadata to IPFS: ${errorText}`);
      }

      const result = await response.json();
      return result.ipfsUri;
    } catch (error) {
      console.error('Error uploading metadata to IPFS:', error);
      throw error;
    }
  };

  // --- Connect only when needed ---
  const ensureWallet = async () => {
    if (!window.ethereum) throw new Error('MetaMask not installed');
    let accounts = await window.ethereum.request({ method: 'eth_accounts' });
    if (!accounts || accounts.length === 0) {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      accounts = await window.ethereum.request({ method: 'eth_accounts' });
    }
    const web3 = new Web3(window.ethereum);
    return { web3, account: accounts[0] };
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const { web3, account } = await ensureWallet();

      let imageUrl = '';
      if (imageFile) {
        imageUrl = await uploadFileToIPFS(imageFile);
      }

      const metadata = {
        name,
        description,
        program_operator: programOperator,
        image: imageUrl,
        external_link: externalLink,
      };

      const uri = await uploadToIPFS(metadata);
      const gasPrice = await web3.eth.getGasPrice();

      const contract = new web3.eth.Contract(contractABI, contractAddress);
      if (!account) throw new Error('No account found. Please connect your MetaMask wallet.');

      const gasLimit = await contract.methods.setContractURI(uri).estimateGas({ from: account });

      await contract.methods.setContractURI(uri).send({
        from: account,
        gas: gasLimit,
        gasPrice
      });

      setMessage(`Provenance Program profile saved successfully! IPFS URI: ${uri}`);
    } catch (error) {
      console.error('Error:', error);
      setMessage('Error saving contract metadata: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container" style={{ marginBottom: "40px" }}>
      {/* ✅ Add this new collection summary section */}
        <h2>About the Provenance Program</h2>
        <p className="field-help">
          This program supports the creation of Product Passports and the recording, tracking 
          and verification of product provenance across the supply chain.
        </p>
        <p>
          <strong> Blockchain-based program contract address:</strong>{' '}
          <a
            href={`https://amoy.polygonscan.com/address/${contractAddress}`}
            target="_blank"
            rel="noreferrer"
          >
            {contractAddress}
          </a>
        </p>
        <p>
          <strong>Notice: </strong>
          <span style={{ color: 'red', fontWeight: 'bold' }}>
            Updating it will replace the current program profile, 
            but will not change previously created Product Passports or their provenance records.
          </span>
        </p>

      <h3>Set the Program Profile</h3>

      <form onSubmit={handleSave}>
        <label>Program Name:</label>
         <p className="field-help">
          Enter a clear name that identifies the product, industry or provenance 
          initiative covered by this program.
        </p>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Bush Apple Provenance Program"
          required
        />
        
        <label>Program Operator:</label>
        <input
          type="text"
          value={programOperator}
          onChange={(e) => setProgramOperator(e.target.value)}
          placeholder="e.g., DSC Nextgen. Ltd."
          required
        />

        <label>Program Description:</label>
        <textarea
          placeholder="e.g., This program supports the creation of Product Passports and the recording of production, harvesting, processing and distribution activities for bush apple products."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />
        <label>Program Logo / Cover Image:</label>
         <p className="field-help">
          Upload an image that represents the program, product, issuing organisation
          or participating community.
        </p>
        <div className="image-input-options">
            <div>
                <label htmlFor="collection-image-upload" className="image-action-label">
                    Choose Image
                </label>

                <input
                    id="collection-image-upload"
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handleImageSelection}
                />
            </div>

            <div>
                <label htmlFor="collection-image-camera" className="image-action-label">
                    Take Photo
                </label>

                <input
                    id="collection-image-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageSelection}
                />
            </div>
        </div>

        {imagePreview && (
            <div className="image-preview">
                <p>Selected collection cover:</p>
                <img
                    src={imagePreview}
                    alt="Collection cover preview"
                />
                <p>{imageFile?.name}</p>
            </div>
        )}

        <label> Governance or Supporting Link (Optional): </label>
        <p className="field-help">
          Provide a link to the program rules, operating organisation, governance
          document, product standard, website or introductory video.
        </p>
        <input
          type="text"
          placeholder="External Link"
          value={externalLink}
          onChange={(e) => setExternalLink(e.target.value)}
        />

        <button type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Create Provenance Program'}
        </button>
      </form>

      {message && <p>{message}</p>}
    </div>
  );
};

export default ApTokenCollection;